import React, { useEffect, useRef, useState } from "react";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import slGoldVideo from "../assets/slgoldvideo.mp4";

gsap.registerPlugin(ScrollTrigger);

const PAGE_BG = "var(--user-bg, #E8D9D5)";
const GOLD = "var(--user-gold, #D4AF37)";
const MAROON = "var(--user-maroon, #2B0000)";

export default function HomeVideoShowcase() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  // AUTO PLAY VIDEO
  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    video.muted = true;

    const playPromise = video.play();

    if (playPromise?.catch) {
      playPromise.catch(() => {
        setIsPlaying(false);
      });
    }
  }, []);

  // GSAP ENTRANCE ANIMATION
  useEffect(() => {
    const section = sectionRef.current;

    if (!section) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        section,
        {
          autoAlpha: 0,
          y: 20,
        },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",

          scrollTrigger: {
            trigger: section,
            start: "top 90%",
            once: true,
          },
        }
      );
    }, section);

    return () => {
      ctx.revert();
    };
  }, []);

  // PLAY / PAUSE
  const togglePlay = async () => {
    const video = videoRef.current;

    if (!video) return;

    if (video.paused) {
      try {
        await video.play();
        setIsPlaying(true);
      } catch (error) {
        console.error("Unable to play video:", error);
      }
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  // MUTE / UNMUTE
  const toggleMute = () => {
    const video = videoRef.current;

    if (!video) return;

    video.muted = !video.muted;

    setIsMuted(video.muted);
  };

  return (
    <section
      ref={sectionRef}
      className="
        relative
        w-full
        overflow-hidden

        h-[190px]

        md:h-[98vh]
        md:min-h-[500px]
      "
      style={{
        backgroundColor: PAGE_BG,
      }}
    >
      {/* ========================= */}
      {/* FULL WIDTH VIDEO */}
      {/* ========================= */}

      <video
        ref={videoRef}
        src={slGoldVideo}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="
          absolute
          inset-0
          h-full
          w-full
          object-cover
          object-center
        "
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* ========================= */}
      {/* VERY LIGHT OVERLAY */}
      {/* ========================= */}

      <div
        className="pointer-events-none absolute inset-0 z-[1]"
        style={{
          background:
            "linear-gradient(180deg, rgba(43,0,0,0.015) 0%, rgba(43,0,0,0) 55%, rgba(43,0,0,0.06) 100%)",
        }}
      />

      {/* ========================= */}
      {/* GOLD BOTTOM EDGE */}
      {/* ========================= */}

      <div
        className="
          pointer-events-none
          absolute
          bottom-0
          left-0
          z-10
          h-[2px]
          w-full
        "
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(212,175,55,0.85), transparent)",
        }}
      />

      {/* ========================= */}
      {/* VIDEO CONTROLS */}
      {/* ========================= */}

      <div
        className="
          absolute
          bottom-3
          right-3
          z-20
          flex
          items-center
          gap-2

          md:bottom-7
          md:right-8
          md:gap-3
        "
      >
        {/* MUTE BUTTON */}

        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? "Unmute video" : "Mute video"}
          className="
            grid
            h-9
            w-9
            place-items-center
            rounded-full
            border
            text-white
            backdrop-blur-md
            transition-all
            duration-300

            hover:scale-105

            md:h-12
            md:w-12
          "
          style={{
            backgroundColor: "rgba(43,0,0,0.40)",
            borderColor: "rgba(212,175,55,0.62)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
          }}
        >
          {isMuted ? (
            <VolumeX className="h-4 w-4 md:h-5 md:w-5" />
          ) : (
            <Volume2 className="h-4 w-4 md:h-5 md:w-5" />
          )}
        </button>

        {/* PLAY / PAUSE BUTTON */}

        <button
          type="button"
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause video" : "Play video"}
          className="
            grid
            h-10
            w-10
            place-items-center
            rounded-full
            border
            text-white
            backdrop-blur-md
            transition-all
            duration-300

            hover:scale-105

            md:h-14
            md:w-14
          "
          style={{
            backgroundColor: "rgba(43,0,0,0.48)",
            borderColor: GOLD,
            boxShadow: "0 10px 28px rgba(0,0,0,0.20)",
          }}
        >
          {isPlaying ? (
            <Pause className="h-4 w-4 md:h-5 md:w-5" />
          ) : (
            <Play className="ml-0.5 h-4 w-4 md:h-5 md:w-5" />
          )}
        </button>
      </div>
    </section>
  );
}